import type { FormActivity } from './IdRelayTypes';

/**
 * Service for handling IdRelay form activities
 */
class IdRelayService {
  /**
   * Fetch form activities from API
   * In production, this would make a real API call
   */
  async getFormActivities(): Promise<FormActivity[]> {
    // Mock API call - in production, replace with actual fetch
    return new Promise((resolve) => {
      setTimeout(() => {
        // Using the provided JSON data as mock response
        const mockData: FormActivity[] = [
          // This would come from the API
          // For now, return empty array - actual data should be loaded from external source
        ];
        resolve(mockData);
      }, 500);
    });
  }

  /**
   * Fetch a specific form activity by ID
   */
  async getFormActivity(activityId: number): Promise<FormActivity | null> {
    const activities = await this.getFormActivities();
    return activities.find((activity) => activity.ActivityId === activityId) || null;
  }

  /**
   * Submit form data to backend API which forwards to IdRelay
   * @param activityId - The activity ID
   * @param activityURL - The unique IdRelay endpoint URL
   * @param formData - The flat dictionary of form field values
   * @returns Promise with submission result
   */
  async submitForm(activityId: number, activityURL: string, formData: Record<string, string>): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch('/api/idrelay/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({
          activityId,
          activityURL,
          formData,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        return {
          success: true,
          message: result.message || 'Form submitted successfully',
        };
      } else {
        const error = await response.json().catch(() => ({ message: 'Submission failed' }));
        return {
          success: false,
          message: error.message || `Server returned ${response.status}`,
        };
      }
    } catch (error) {
      console.error('Form submission error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error occurred',
      };
    }
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get all unique interest fields based on selected roles
   */
  getInterestsForRoles(selectedRoles: string[], fieldLayout: Array<{ TriggerId: string; ImpactIds: string[] }>): string[] {
    const allInterests = new Set<string>();

    selectedRoles.forEach((role) => {
      const layout = fieldLayout.find((item) => item.TriggerId === role);
      if (layout) {
        layout.ImpactIds.forEach((interest) => allInterests.add(interest));
      }
    });

    return Array.from(allInterests);
  }
}

export const idRelayService = new IdRelayService();
